import { Stack, SxProps, Theme, Typography } from '@mui/material';

import { AlertCircleIcon } from '@/components/icons/AlertCircleIcon';
import { Link01Icon } from '@/components/icons/Link01Icon';
import { Card } from '@/components/layout/Card/Card';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { COLORS } from '@/styles/tokens/colors';
import { WorkflowTrackerStatus } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import {
  formatDateToMMDDYY,
  formatDateToMonDDYYYYWithTime,
} from '@/utils/formatting/dates';

function getSyncStatusLabel(lastSyncStatus: WorkflowTrackerStatus) {
  switch (lastSyncStatus) {
    case WorkflowTrackerStatus.Queued:
    case WorkflowTrackerStatus.Started:
      return 'Syncing';
    case WorkflowTrackerStatus.Failed:
      return 'Sync';
    case WorkflowTrackerStatus.Completed:
      return 'Synced';
    default:
      throw new UnreachableError({
        case: lastSyncStatus,
        message: `Unknown sync status: ${lastSyncStatus}`,
      });
  }
}

export interface EntitySyncBadgeProps {
  lastSyncedAt: Date | null;
  lastSyncStatus: WorkflowTrackerStatus;
  sx?: SxProps<Theme>;
}

export function EntitySyncBadge({
  lastSyncStatus,
  lastSyncedAt,
  sx = {},
}: EntitySyncBadgeProps) {
  const syncStatusLabel = getSyncStatusLabel(lastSyncStatus);

  const cardContent = (
    <Card variant="filled-callout" sx={{ p: 1, px: 1.5, ...sx }}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <EntitySyncIndicator
          lastSyncFailed={lastSyncStatus === WorkflowTrackerStatus.Failed}
        />
        <Stack>
          <Typography variant="title-s" component="div">
            {syncStatusLabel}
          </Typography>
          <Typography variant="body-xs" component="div">
            {lastSyncedAt
              ? formatDateToMMDDYY(lastSyncedAt)
              : EMPTY_CONTENT_HYPHEN}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  );

  return lastSyncedAt ? (
    <Tooltip
      title={`Last synced at ${formatDateToMonDDYYYYWithTime(lastSyncedAt)}`}
    >
      {cardContent}
    </Tooltip>
  ) : (
    cardContent
  );
}

interface EntitySyncFailedIndicatorProps {
  lastSyncFailed: boolean;
}

const ENTITY_SYNC_INDICATOR_DIMENSIONS = 20;
function EntitySyncIndicator({
  lastSyncFailed,
}: EntitySyncFailedIndicatorProps) {
  if (lastSyncFailed) {
    return (
      <Tooltip title="The most recent attempted sync failed. Please contact your administrator or reach out to support@withluminary.com.">
        <AlertCircleIcon
          size={ENTITY_SYNC_INDICATOR_DIMENSIONS}
          sx={{ color: COLORS.FUNCTIONAL.ERROR[700] }}
        />
      </Tooltip>
    );
  }

  return (
    <Link01Icon
      size={ENTITY_SYNC_INDICATOR_DIMENSIONS}
      sx={{ color: COLORS.TEXT.SECONDARY }}
    />
  );
}
